import React from 'react';
import { motion } from 'framer-motion';
import { GraduationCap, BookMarked, MapPin, Info } from 'lucide-react';
import meenammalogobg from '@/images/meenammalogobg.png';

// ── Variants ──────────────────────────────────────────────────────────────────
const fadeUp   = { hidden: { opacity: 0, y: 24 }, visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.12, duration: 0.55, ease: 'easeOut' } }) };
const slideIn  = (dir) => ({ hidden: { opacity: 0, x: dir === 'left' ? -40 : 40 }, visible: { opacity: 1, x: 0, transition: { duration: 0.65, ease: [0.25, 0.46, 0.45, 0.94] } } });

// ── Data ───────────────────────────────────────────────────────────────────────
const FEATURES = [
  {
    Icon: GraduationCap,
    title: 'Academic Scholarships',
    desc: 'Merit-based financial assistance to talented students from underprivileged communities — removing economic barriers to higher education.',
    color: 'text-brand-indigo',
    bg: 'bg-brand-indigo/10',
    border: 'border-brand-indigo/15',
  },
  {
    Icon: BookMarked,
    title: 'Learning Resources',
    desc: 'Essential educational materials and academic guidance — ensuring every student has the tools they need for uninterrupted learning.',
    color: 'text-brand-emerald',
    bg: 'bg-brand-emerald/10',
    border: 'border-brand-emerald/15',
  },
  {
    Icon: MapPin,
    title: 'Regional Impact',
    desc: 'Concentrating efforts on empowering students throughout the Srikakulam region of Andhra Pradesh, driving lasting community change.',
    color: 'text-brand-amber',
    bg: 'bg-brand-amber/10',
    border: 'border-brand-amber/15',
  },
];

// ── Feature Card ───────────────────────────────────────────────────────────────
const FeatureCard = ({ Icon, title, desc, color, bg, border, index }) => (
  <motion.div
    custom={index}
    initial="hidden"
    whileInView="visible"
    variants={fadeUp}
    viewport={{ once: true, amount: 0.3 }}
    whileHover={{ y: -5, scale: 1.01 }}
    transition={{ type: 'spring', stiffness: 300, damping: 20 }}
  >
    <div className={`h-full glassmorphism-card border ${border} rounded-2xl p-7
                     card-glow-hover card-shine group transition-all duration-300`}>
      {/* Icon */}
      <div className={`inline-flex items-center justify-center w-13 h-13 rounded-2xl ${bg} mb-5
                       group-hover:scale-110 transition-transform duration-300`}>
        <Icon className={`h-6 w-6 ${color}`} />
      </div>

      {/* Title */}
      <h3 className={`text-base font-bold mb-2.5 ${color}`}>{title}</h3>

      {/* Description */}
      <p className="text-sm text-muted-foreground leading-relaxed">{desc}</p>
    </div>
  </motion.div>
);

// ── Component ──────────────────────────────────────────────────────────────────
const AboutSection = () => (
  <section id="about" className="py-20 md:py-28 bg-background">
    <div className="container mx-auto px-4 sm:px-6">

      {/* Section badge + Header */}
      <motion.div
        className="text-center mb-16"
        initial="hidden"
        whileInView="visible"
        variants={fadeUp}
        viewport={{ once: true, amount: 0.3 }}
      >
        <div className="flex justify-center mb-4">
          <span className="section-badge">
            <Info className="h-3 w-3" />
            Our Story
          </span>
        </div>
        <h2 className="text-3xl md:text-4xl font-bold mb-5">
          About{' '}
          <span className="gradient-text">Meenamma Educational Trust</span>
        </h2>
        <p className="text-muted-foreground text-lg max-w-3xl mx-auto leading-relaxed">
          Our trust stands committed to nurturing academic excellence by extending financial
          assistance and educational resources to deserving students across economically
          disadvantaged backgrounds.
        </p>
      </motion.div>

      {/* Two-column content */}
      <div className="grid md:grid-cols-2 gap-12 items-center mb-16">

        {/* Logo showcase */}
        <motion.div
          className="relative flex items-center justify-center p-10 rounded-3xl overflow-hidden
                     border border-border/50 shadow-lg aspect-video"
          style={{
            background: 'linear-gradient(135deg, hsl(var(--brand-indigo)/0.08) 0%, hsl(var(--brand-emerald)/0.06) 50%, hsl(var(--brand-amber)/0.08) 100%)',
          }}
          initial="hidden"
          whileInView="visible"
          variants={slideIn('left')}
          viewport={{ once: true, amount: 0.3 }}
        >
          {/* Decorative circles */}
          <div className="absolute top-4 right-4 w-20 h-20 rounded-full bg-brand-indigo/6 blur-xl" />
          <div className="absolute bottom-4 left-4 w-16 h-16 rounded-full bg-brand-emerald/8 blur-xl" />

          <motion.img
            src={meenammalogobg}
            alt="Meenamma Educational Trust Logo"
            className="h-40 w-auto object-contain relative z-10"
            whileHover={{ scale: 1.06 }}
            transition={{ type: 'spring', stiffness: 250, damping: 20 }}
          />
        </motion.div>

        {/* Text content */}
        <motion.div
          className="space-y-5"
          initial="hidden"
          whileInView="visible"
          variants={slideIn('right')}
          viewport={{ once: true, amount: 0.3 }}
        >
          <p className="text-muted-foreground leading-relaxed text-[15px]">
            Our fundamental goal is to bridge the educational gap by providing holistic support
            to students, ensuring they can pursue their academic aspirations without financial
            barriers. We firmly believe that every child deserves access to quality education
            regardless of their economic circumstances.
          </p>
          <p className="text-muted-foreground leading-relaxed text-[15px]">
            We are dedicated to identifying and nurturing exceptional talent through our
            scholarship programs, prioritising students from economically challenged families
            and those with limited resources. Our operations are guided by integrity and
            unwavering dedication to the Srikakulam community.
          </p>

          {/* Stats row */}
          <div className="pt-3 grid grid-cols-3 gap-4 border-t border-border/50">
            {[
              { value: '2024', label: 'Est. Year', color: 'text-brand-indigo'  },
              { value: 'SKL',  label: 'Region',    color: 'text-brand-emerald' },
              { value: '100%', label: 'Integrity', color: 'text-brand-amber'   },
            ].map(({ value, label, color }) => (
              <div key={label} className="text-center">
                <p className={`text-xl font-extrabold tracking-tight ${color}`}>{value}</p>
                <p className="text-xs text-muted-foreground mt-0.5 font-medium">{label}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Feature cards */}
      <div className="grid sm:grid-cols-3 gap-5">
        {FEATURES.map((f, i) => (
          <FeatureCard key={f.title} {...f} index={i} />
        ))}
      </div>
    </div>
  </section>
);

export default AboutSection;
